import { FsEvent } from '@fullstory/fs-script-api';
import { UID } from '@fullstory/util/uid';
import {
  applyRequestBuilderSettings,
  buildXhrPost,
  NetworkController,
  Request,
  RequestBuilderSettings,
  RequestFailure,
  requestSettingsBuilder,
} from '@fullstory/util/xhr';
import { Query, QueryClient } from '@tanstack/react-query';
import { isArray, isFunction, isString } from 'lodash';
import Cache from 'lru-cache';
import {
  CacheKey,
  CacheKeyFunction,
  CacheKeyPredicate,
  GRPCFunction,
  GRPCRequestConfig,
  GRPCReturn,
  RequestConfig,
} from './types';
import { cacheKeyToQueryKey, defaultCacheKey, stringifyQueryKey } from './utils';

type RequestDescriptor = { inFlight: boolean; promise: Promise<any> };
type RequestCache = Cache<string, RequestDescriptor>;
type BatchIdMap = Record<string, Set<string>>;

export interface CommonRequest {
  request: GRPCReturn<any>;
  cacheKey?: CacheKeyFunction;
  cache: RequestCache;
  batchIdMap: BatchIdMap;
  body: any;
  params?: any;
  force?: boolean;
  modify?: (val: any) => any;
  maxAge?: number;
  invalidateCacheKey?: CacheKeyPredicate;
  batchId: string;
  readWrite: 'read' | 'write';
  baseRequest: Request;
}

export interface ExecuteRequestOptions {
  onCacheEntriesInvalidated?: (predicate: CacheKeyPredicate) => void;
  onCacheEntriesUpdated?: <T>(predicate: CacheKeyPredicate, data: T) => void;
  onCacheEntryInvalidated?: (key: CacheKey) => void;
  onCacheEntryUpdated?: <T>(key: CacheKey, data: T) => void;
}

export const buildExecuteRequestOptions = (queryClient: QueryClient) => {
  const matchCachePredicate = (query: Query, predicate: CacheKeyPredicate) => {
    const { queryKey } = query;

    // When predicate is a string, we wrap it in an array to create a QueryKey and
    // compare it to the query.queryKey
    if (isString(predicate)) {
      const predicateQueryKey = cacheKeyToQueryKey(predicate);
      const predicateQueryKeyString = stringifyQueryKey(predicateQueryKey);
      const queryKeyString = stringifyQueryKey(queryKey);
      return (
        predicateQueryKeyString === queryKeyString &&
        queryKeyString !== undefined &&
        queryKeyString !== undefined
      );
    }

    // When predicate is already an array, we need to build multiple QueryKeys
    // and compare each one to the query.queryKey
    if (isArray(predicate)) {
      return predicate.some(cacheKey => {
        const predicateQueryKey = cacheKeyToQueryKey(cacheKey);
        const predicateQueryKeyString = stringifyQueryKey(predicateQueryKey);
        const queryKeyString = stringifyQueryKey(queryKey);
        return (
          predicateQueryKeyString === queryKeyString &&
          queryKeyString !== undefined &&
          queryKeyString !== undefined
        );
      });
    }

    // Finally, when predicate is a function, we need to convert the QueryKey
    // into a cache key that it can check.
    if (isFunction(predicate)) {
      // NB: this is an imperfect heuristic because it doesn't handle non-string entries,
      // but it's likely to cache simple cases where the QueryKey is a hash of the request body.
      const queryKeyString = queryKey.filter(isString).join('');
      return predicate(queryKeyString);
    }

    return false;
  };

  const options: ExecuteRequestOptions = {
    onCacheEntriesInvalidated(predicate) {
      queryClient.invalidateQueries({
        predicate: query => matchCachePredicate(query, predicate),
      });
    },
    onCacheEntriesUpdated(predicate, data) {
      // NB: setQueryData will create an entry if it doesn't exist, but setQueriesData will not.
      if (isString(predicate)) {
        queryClient.setQueryData(cacheKeyToQueryKey(predicate), data);
      } else {
        queryClient.setQueriesData(
          {
            predicate: query => matchCachePredicate(query, predicate),
          },
          data,
        );
      }
    },
    onCacheEntryInvalidated(cacheKey) {
      queryClient.invalidateQueries({
        queryKey: cacheKeyToQueryKey(cacheKey),
      });
    },
    onCacheEntryUpdated(cacheKey, data) {
      // NB: setQueryData will create an entry if it doesn't exist, but setQueriesData will not.
      queryClient.setQueryData(cacheKeyToQueryKey(cacheKey), data);
    },
  };

  return options;
};

export const getCacheKey = (request: CommonRequest): string => {
  const cacheKeyFn = request.cacheKey ?? defaultCacheKey;
  return cacheKeyFn(request.request.endpoint, request.body, request.params);
};

// We only want to fire the cache full event once, so fire if the cache is full and this is false. Then
// we set it to true so it won't fire again.
let cacheFullEventFired = false;

// Given a common request format, send the request, handle the caching, and save or read from the cache.
function sendCommonRequest(
  commonRequest: CommonRequest,
  options?: ExecuteRequestOptions,
): {
  promise: Promise<any>;
  cacheMiss: boolean;
} {
  const {
    request,
    cache,
    batchIdMap,
    force,
    modify = i => i,
    maxAge,
    invalidateCacheKey,
    batchId,
  } = commonRequest;
  const key = getCacheKey(commonRequest);
  const entry = cache.get(key);
  const cacheMiss = entry == null || !!(force && !entry.inFlight);

  if (cacheMiss) {
    // We keep track of all cache keys associated with a given batch ID
    let batchIdCache = batchIdMap[batchId];
    if (batchIdCache == null) {
      batchIdCache = new Set<string>();
      batchIdMap[batchId] = batchIdCache;
    }
    batchIdCache.add(key);

    const promise = request
      .promise()
      .then(modify)
      .then(data => {
        options?.onCacheEntryUpdated?.(key, data);
        // NB: the onCacheEntriesInvalidaed callback is used to update the React Query cache,
        // and as a consequence, triggers downstream refetches.
        //
        // The legacy implementation of invalidateCacheKey eagerly invalidated the cache before
        // the request finished, which is a mistake that often leads to discarding fresh cache entries.
        // For newer code paths (triggered via the callback below), we will only invalidate cache
        // entries when the promise resolves and the request was successful.
        //
        // This is a breaking change that will occasionally lead to out-of-sync caches, but the risk of
        // downstream issues is low and worth the benefit of ensuring proper cache invalidation.
        // It's important to ensure that cached entries are actually stale before invalidating them.
        if (invalidateCacheKey) {
          options?.onCacheEntriesInvalidated?.(invalidateCacheKey);
        }
        return data;
      })
      .catch(err => {
        // If the request fails, we remove it from cache so that it can be retried
        cache.del(key);

        throw err;
      })
      .finally(() => {
        const entry = cache.peek(key);

        if (entry != null) entry.inFlight = false;

        // We only need to know about in-flight batch IDs that might get cancelled, so we clean them
        // up here.
        const keys = batchIdMap[batchId];

        if (keys != null) {
          keys.delete(key);

          if (keys.size === 0) delete batchIdMap[batchId];
        }
      });

    // Note: onCacheEntryUpdated is called in the .then() above, so we don't need to call it here.
    cache.set(key, { promise, inFlight: true }, maxAge);

    const CACHE_FULL_EVENT = 'Client cache full';
    if (cache.length >= cache.max && !cacheFullEventFired) {
      FsEvent.recordCustomEventV2(
        CACHE_FULL_EVENT,
        {
          timestamp: new Date().toISOString(),
        },
        { timestamp: 'str' }, // timestamp will be inferred as 'date' if not set to 'str'
      );
      cacheFullEventFired = true;
    }

    if (invalidateCacheKey) {
      if (isString(invalidateCacheKey) || isArray(invalidateCacheKey)) {
        const keys = [invalidateCacheKey].flat();

        for (const key of keys) {
          cache.del(key);
        }
      } else if (isFunction(invalidateCacheKey)) {
        cache.forEach((_, cacheKey) => {
          if (invalidateCacheKey(cacheKey)) {
            cache.del(cacheKey);
          }
        });
      }
    }

    return {
      promise,
      cacheMiss,
    };
  }

  return {
    promise: entry!.promise,
    cacheMiss,
  };
}

export function requestToGRPCReturn(request: Request, body: any): GRPCReturn<any> {
  return {
    promise: () =>
      new Promise((resolve, reject) => {
        request
          .Success((_, response) => resolve(response))
          .Fail((status, response, safeMessage) => {
            // a status of 0 represents a cancellation
            if (status > 0) {
              console.error(
                `Request on ${
                  request.url
                } failed with status ${status} and response ${JSON.stringify(response, null, 2)}`,
              );
            }
            reject(<RequestFailure>{ status, response, safeMessage });
          })
          .Send(body);
      }),
    endpoint: request.url,
  };
}

const requestConfigToCommonRequest = ({
  cache,
  batchIdMap,
  xhr,
  requestConfig,
  cacheKey,
}: {
  cache: RequestCache;
  batchIdMap: BatchIdMap;
  xhr: NetworkController;
  requestConfig: RequestConfig<any, any> & { modify?: (rsp: any) => any };
  cacheKey?: (url: string) => string;
}): CommonRequest => {
  const preparedRequest = applyRequestBuilderSettings(
    requestSettingsBuilder(xhr, requestConfig.request.buildRequest),
    requestConfig.request.buildRequest,
  );
  const baseRequest = preparedRequest.BuildRequest();
  const body = requestConfig.request.body;
  const request = requestToGRPCReturn(baseRequest, body);

  return {
    request,
    cacheKey,
    cache,
    batchIdMap,
    body,
    params: requestConfig.request.buildRequest.params,
    force: true,
    invalidateCacheKey: requestConfig.request.invalidateCacheKey,
    batchId: preparedRequest.BatchId(),
    readWrite: 'write', // N/A
    baseRequest,
    modify: requestConfig.modify,
  };
};

function fakeSend<R>(): Promise<R> {
  return Promise.reject('Should not be used');
}

const grpcRequestConfigToCommonRequest = <TBody, TResponse>({
  cache,
  batchIdMap,
  xhr,
  body,
  grpcFunction,
  settings,
  invalidateCacheKey,
  cacheKey,
  modify,
}: {
  cache: RequestCache;
  batchIdMap: BatchIdMap;
  xhr: NetworkController;
  body?: TBody;
  grpcFunction: GRPCFunction<TBody, TResponse>;
  settings?: RequestBuilderSettings;
  invalidateCacheKey?: CacheKeyPredicate;
  cacheKey?: (url: string) => string;
  modify?: (rsp: TResponse) => any;
}): CommonRequest => {
  // we only need the endpoint, so we use this 'fakeSend' to get access to it
  const endpoint = grpcFunction(fakeSend, body).endpoint;
  // we need to construct our own promise, so we can have access to the `Request`
  // to have the option to cancel it later
  const { request, promise } = buildXhrPost(xhr, endpoint, body, settings);

  return {
    request: {
      promise,
      endpoint,
    },
    cacheKey,
    cache,
    batchIdMap,
    body,
    force: true,
    invalidateCacheKey,
    batchId: settings?.batchOptions?.id ?? '',
    readWrite: 'write', // N/A
    baseRequest: request,
    modify,
  };
};

export const cache = new Cache<string, RequestDescriptor>({ max: 250 });
export const batchIdMap: BatchIdMap = {};
export const requestMap: Record<
  string,
  {
    request: Request;
    cacheKey: string;
  }
> = {};

type ExecuteRequestArgs<D> = {
  commonRequest: CommonRequest;
  id: string;
  options?: ExecuteRequestOptions;
  events?: {
    onStart: (cacheMiss: boolean) => void;
    onSuccess: (data: D, cacheMiss: boolean) => void;
    onError: (cacheMiss: boolean, error: unknown) => void;
  };
};

export async function executeRequest<M>({
  commonRequest,
  id,
  options,
  events,
}: ExecuteRequestArgs<M>) {
  let data: M;

  const { promise, cacheMiss } = sendCommonRequest(commonRequest, options);

  events?.onStart(cacheMiss);

  if (cacheMiss) {
    requestMap[id] = {
      request: commonRequest.baseRequest,
      cacheKey: getCacheKey(commonRequest),
    };
  }

  try {
    data = await promise;
  } catch (error) {
    delete requestMap[id];
    events?.onError(cacheMiss, error);
    throw error;
  }
  delete requestMap[id];
  events?.onSuccess(data, cacheMiss);

  return data;
}

type ProcessRequestBaseArgs = {
  /**
   * Network controller to use for the request.
   */
  xhr: NetworkController;
  /**
   * An optional ID to associate with the request. If not provided, a unique ID will be generated.
   *
   * Can be used to cancel the request later.
   */
  id?: string;
  /**
   * Hooks into cache updates.
   */
  options?: ExecuteRequestOptions;
};

type LegacyRequestConfig<TBody, TResponse> = RequestConfig<TBody, TResponse> & {
  // NB: Modify is a deprecated option that may be present on some legacy configs generated
  // from redux actions. It is not recommended to use this option, so it's hidden as part of
  // the internal implementation.
  modify?: (rsp: TResponse) => any;
};

export async function processRequest<TBody, TResponse>({
  requestConfig,
  xhr,
  id = UID(),
  options,
}: ProcessRequestBaseArgs & {
  requestConfig: LegacyRequestConfig<TBody, TResponse>;
}) {
  return executeRequest<TResponse>({
    commonRequest: requestConfigToCommonRequest({
      cache,
      batchIdMap,
      xhr,
      requestConfig,
      cacheKey: requestConfig.cacheKey,
    }),
    id,
    options,
  });
}

type LegacyGRPCRequestConfig<TBody, TResponse> = GRPCRequestConfig<TBody, TResponse> & {
  // NB: Modify is a deprecated option that may be present on some legacy configs generated
  // from redux actions. It is not recommended to use this option, so it's hidden as part of
  // the internal implementation.
  modify?: (rsp: TResponse) => any;
};

export async function processGRPCRequest<TBody, TResponse>({
  requestConfig,
  xhr,
  id = UID(),
  options,
}: ProcessRequestBaseArgs & {
  requestConfig: LegacyGRPCRequestConfig<TBody, TResponse>;
}) {
  return executeRequest<TResponse>({
    commonRequest: grpcRequestConfigToCommonRequest({
      cache,
      batchIdMap,
      xhr,
      body: requestConfig.request.body,
      grpcFunction: requestConfig.request.grpcFunction,
      settings: requestConfig.request.settings,
      invalidateCacheKey: requestConfig.request.invalidateCacheKey,
      cacheKey: requestConfig.cacheKey,
      modify: requestConfig.modify,
    }),
    id,
    options,
  });
}
